import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Pencil, Trash2, Eye, RotateCcw, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/apiClient';
import type { User, Plan } from '@/_types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import CreditPaymentModal from '@/components/users/CreditPaymentModal';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchUsers(page: number, search: string, planSlug: string) {
  const res = await apiClient.get('/users', {
    params: {
      page,
      search: search || undefined,
      per_page: 15,
      plan_slug: planSlug || undefined,
    },
  });
  return res.data;
}

async function deleteUser(id: string) {
  await apiClient.delete(`/users/${id}`);
}

async function restoreUser(id: string) {
  await apiClient.post(`/users/${id}/restore`);
}

async function fetchPlans(): Promise<Plan[]> {
  const res = await apiClient.get('/plans', { params: { per_page: 100 } });
  return res.data?.data?.plans ?? [];
}

// ---------------------------------------------------------------------------
// UsersPage
// ---------------------------------------------------------------------------

export default function UsersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [planSlug, setPlanSlug] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [creditModalUser, setCreditModalUser] = useState<User | null>(null);

  // ── Fetch users ────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, search, planSlug],
    queryFn: () => fetchUsers(page, search, planSlug),
  });

  // ── Fetch plans for filter dropdown ───────────────────────────────────
  const { data: plans } = useQuery({
    queryKey: ['admin-plans-filter'],
    queryFn: fetchPlans,
  });

  // ── Delete mutation ────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      toast.success('User deleted');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setDeleteId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Restore mutation ───────────────────────────────────────────────────
  const restoreMutation = useMutation({
    mutationFn: restoreUser,
    onSuccess: () => {
      toast.success('User restored');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const users: User[] = data?.data?.users ?? [];
  const meta = data?.meta;

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handlePlanFilter = (value: string) => {
    setPlanSlug(value === '_all' ? '' : value);
    setPage(1);
  };

  const isSuperAdmin = (user: User): boolean =>
    user.roles.some((r) => r.slug === 'super-admin');

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-sm text-muted-foreground">Manage all users across organizations</p>
        </div>
        <Button onClick={() => navigate('/users/create')}>
          <Plus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Search + Plan filter */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by name or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <Button variant="outline" onClick={handleSearch}>
          Search
        </Button>

        {/* Plan filter */}
        <div className="w-44">
          <Select value={planSlug || '_all'} onValueChange={handlePlanFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All Plans" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Plans</SelectItem>
              {(plans ?? []).map((plan) => (
                <SelectItem key={plan.id} value={plan.slug}>
                  {plan.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                Organization
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Source</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Plan</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Roles</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const isDeleted = !!user.deleted_at;
                const superAdmin = isSuperAdmin(user);

                return (
                  <tr
                    key={user.id}
                    className={`hover:bg-muted/20 transition-colors ${isDeleted ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3 font-medium">{user.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {user.organization?.name ?? (
                        <span className="italic text-xs">System</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.auth_source === 'google' ? (
                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200">
                          Google
                        </Badge>
                      ) : user.auth_source === 'microsoft' ? (
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200">
                          Microsoft
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Normal</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.organization?.plan ? (
                        <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 border-violet-200 capitalize">
                          {user.organization.plan}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {user.roles.length > 0 ? (
                          user.roles.map((r) => (
                            <Badge key={r.id} variant="secondary" className="text-xs">
                              {r.title}
                            </Badge>
                          ))
                        ) : user.org_role ? (
                          <Badge variant="outline" className="text-xs capitalize">
                            {user.org_role}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {isDeleted ? (
                        <Badge variant="destructive">Deleted</Badge>
                      ) : (
                        <Badge variant={user.is_active ? 'success' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* View button — always visible */}
                        <Button
                          variant="ghost"
                          size="icon"
                          title="View user"
                          onClick={() => navigate(`/users/${user.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>

                        {/* Edit + Delete/Restore — only for non-deleted users */}
                        {!isDeleted && (
                          <>
                            {/* Credit purchase — only for org owners */}
                            {user.org_role === 'owner' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Send credit payment link"
                                onClick={() => setCreditModalUser(user)}
                              >
                                <Crown className="h-4 w-4 text-amber-500" />
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              size="icon"
                              title="Edit user"
                              onClick={() => navigate(`/users/${user.id}/edit`)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>

                            {/* Hide delete for super-admin users */}
                            {!superAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                title="Delete user"
                                onClick={() => setDeleteId(user.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}

                        {/* Restore button — only for deleted users */}
                        {isDeleted && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Restore user"
                            disabled={restoreMutation.isPending}
                            onClick={() => restoreMutation.mutate(user.id)}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {meta.current_page} of {meta.last_page} ({meta.total} users)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!meta.has_more}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Credit payment modal */}
      <CreditPaymentModal
        user={creditModalUser}
        onClose={() => setCreditModalUser(null)}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete the user. You can restore them later from the users list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
